import { SupplierGetResponseType } from "@sellar-io/api-v2/types";
import { pluralize } from "@sellar-io/common";
import { Icon } from "@sellar-io/components";
import { ReactNode } from "react";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/shallow";
import { DISCOVER_SEARCH } from "~/constants/routes";
import { useDiscoveryV2SearchStore } from "~/pages/discovery/v2/DiscoveryV2SearchStore";
import { useDraftDbsBaskets } from "~/pages/discovery/v2/distribution/useDraftDbsBaskets";
import { useCanDisplayStorefrontConsolidatedBasketBanner } from "./useCanDisplayStorefrontConsolidatedBasketBanner";
import { useMediaQuery } from "usehooks-ts";
import { twMerge } from "tailwind-merge";

export const StorefrontConsolidatedBasketBanner = ({
  supplier,
}: {
  supplier: SupplierGetResponseType;
}) => {
  const canDisplayConsolidatedBasketBanner =
    useCanDisplayStorefrontConsolidatedBasketBanner(supplier);

  if (!canDisplayConsolidatedBasketBanner) return null;

  return <StorefrontConsolidatedBasketBannerContent supplier={supplier} />;
};

const StorefrontConsolidatedBasketBannerContent = ({
  supplier,
}: {
  supplier: Pick<SupplierGetResponseType, "id" | "Business">;
}) => {
  const { baskets } = useDraftDbsBaskets();
  const navigate = useNavigate();

  const basketDraftOrders = baskets.flatMap((basket) => basket.DraftOrders);

  const supplierBasketDraftOrderItems = basketDraftOrders.flatMap((order) =>
    order.Items.filter(() => order.SupplierId === supplier.id),
  );

  const supplierName =
    supplier.Business.name || supplier.Business.registeredCompanyName || "";

  const isSupplierInConsolidatedBasket =
    supplierBasketDraftOrderItems.length > 0;

  const nbSupplierItemsInBasket = supplierBasketDraftOrderItems.length;

  const {
    setSearchValue,
    setIsDeliveredBySellar,
    setDeliveredBySellarOption,
    setSelectedSuppliers,
    reset,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      setIsDeliveredBySellar: state.setIsDeliveredBySellar,
      setDeliveredBySellarOption: state.setDeliveredBySellarOption,
      setSearchValue: state.setSearchValue,
      setSelectedSuppliers: state.setSelectedSuppliers,
      reset: state.reset,
    })),
  );

  const handleGoToMarket = () => {
    reset();
    setSearchValue("");
    setIsDeliveredBySellar(true);
    setDeliveredBySellarOption("all");
    setSelectedSuppliers([
      {
        id: supplier.id,
        name: supplierName,
      },
    ]);

    navigate(DISCOVER_SEARCH);
  };

  return (
    <div className="flex flex-col p-2">
      {!isSupplierInConsolidatedBasket && (
        <StorefrontConsolidatedBasketBannerAlert
          icon={<Icon icon="CART_OUTLINE" className="text-blue-800" />}
          title="You already have a basket in progress on the Market"
          description={`Your ${supplierName} order can arrive together with your other Delivered by Sellar items on the Market`}
          onClickGoToMarket={handleGoToMarket}
        />
      )}
      {isSupplierInConsolidatedBasket && (
        <StorefrontConsolidatedBasketBannerAlert
          icon={
            <p className="text-sm font-semibold text-blue-800">
              {nbSupplierItemsInBasket}x
            </p>
          }
          variant="alternative"
          title={
            <p className="items-center gap-1">
              You have{" "}
              <span className="text-blue-800 underline">
                {nbSupplierItemsInBasket}{" "}
                {pluralize("item", nbSupplierItemsInBasket)}
              </span>{" "}
              from {supplierName} already in your basket
            </p>
          }
          description="Continue building your basket on the Market to get this order as well as others delivered together by Sellar"
          onClickGoToMarket={handleGoToMarket}
        />
      )}
    </div>
  );
};

type StorefrontConsolidatedBasketBannerAlertProps = {
  icon: ReactNode;
  title: ReactNode;
  description: string;
  variant?: "default" | "alternative";
  onClickGoToMarket: () => void;
};
const StorefrontConsolidatedBasketBannerAlert = ({
  icon,
  title,
  description,
  variant = "default",
  onClickGoToMarket,
}: StorefrontConsolidatedBasketBannerAlertProps) => {
  const isMobile = useMediaQuery("(max-width: 700px)");

  return (
    <div
      className={twMerge(
        "flex items-center gap-4 rounded-lg border p-4",
        variant === "default" && "border-blue-200 bg-blue-100",
        variant === "alternative" && "border-blue-400 bg-blue-200",
      )}
    >
      <div
        className={twMerge(
          "flex h-9 w-9 items-center justify-center rounded-lg border",
          isMobile && "self-start",
          variant === "default" && "border-blue-300 bg-blue-200",
          variant === "alternative" && "border-blue-400 bg-blue-300",
        )}
      >
        {icon}
      </div>
      <div
        className={twMerge(
          "flex flex-1 gap-2",
          isMobile && "flex-col",
          !isMobile && "flex-row",
        )}
      >
        <div className="flex flex-1 flex-col">
          <div
            className={twMerge(
              "text-sm text-zinc-800",
              !isMobile && "font-semibold",
            )}
          >
            {title}
          </div>
          {!isMobile && (
            <div className="text-sm font-normal text-zinc-600">
              {description}
            </div>
          )}
        </div>
        <button
          type="button"
          className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-blue-800"
          onClick={onClickGoToMarket}
        >
          {isMobile && variant === "alternative"
            ? "Continue ordering on the Market"
            : "Go to Market"}
          <Icon icon="CHEVRON_RIGHT" />
        </button>
      </div>
    </div>
  );
};
